import React, { useState, useEffect, useContext, createContext } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  FiUsers, FiPackage, FiGrid, FiDollarSign,
  FiCheckCircle, FiClock, FiAlertCircle, FiBell, FiRefreshCw,
  FiArrowUpRight, FiActivity, FiLayers, FiUserCheck,
  FiCalendar, FiStar,
} from "react-icons/fi";

const API_URL = "https://estatebackend.pixelmindsolutions.com/api/admin-dashboard/stats";

/* ─────────────────────────────────────────
   THEME CONTEXT
   Provides a single `dm` boolean + helper `t()`
   t(light, dark) → returns the right class string
───────────────────────────────────────── */
const ThemeCtx = createContext(false);
const useTheme = () => {
  const dm = useContext(ThemeCtx);
  /** t(lightClass, darkClass) — pick the right one */
  const t = (light, dark) => (dm ? dark : light);
  return { dm, t };
};

/* ─── Helpers ─── */
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

const getInitials = (name) =>
  name ? name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase() : "?";

const AVATAR_BG = ["#dc2626","#b91c1c","#c2410c","#1d4ed8","#15803d","#7c3aed","#0369a1","#9a3412"];
const getAvatarColor = (id) => AVATAR_BG[(id?.charCodeAt(0) || 0) % AVATAR_BG.length];

/* ─── Animated Counter ─── */
const Counter = ({ target, duration = 1100 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) return;
    const step = target / (duration / 16);
    let cur = 0;
    const timer = setInterval(() => {
      cur = Math.min(cur + step, target);
      setVal(Math.floor(cur));
      if (cur >= target) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  return <>{val.toLocaleString()}</>;
};

/* ─── Chart Tooltip ─── */
const ChartTooltip = ({ active, payload, label }) => {
  const { t } = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div className={`rounded-xl shadow-xl px-4 py-3 text-xs border
      ${t("bg-white border-red-100 text-gray-700", "bg-gray-900 border-red-900 text-gray-200")}`}>
      <p className="font-bold uppercase tracking-wider mb-1 text-gray-400">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="mt-0.5 font-semibold" style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

/* ─── Stat Card ─── */
const StatCard = ({ icon: Icon, label, value, sub, trend, iconBg, iconBgDark, iconColor, delay = 0 }) => {
  const { t } = useTheme();
  return (
    <div
      className={`rounded-2xl border shadow-sm hover:shadow-md hover:-translate-y-0.5
        transition-all duration-200 p-5 flex flex-col gap-3 relative overflow-hidden group
        ${t(
          "bg-white border-red-50",
          "bg-gray-900 border-gray-700/60"
        )}`}
      style={{ animationDelay: `${delay}ms`, animation: "fadeUp 0.45s ease both" }}
    >
      {/* top shimmer on hover */}
      <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-red-400 to-transparent
        opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center
          ${t(iconBg, iconBgDark || iconBg.replace("50","900/60"))}`}>
          <Icon size={18} className={iconColor} />
        </div>
        {trend && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full border
            ${t(
              "text-emerald-600 bg-emerald-50 border-emerald-100",
              "text-emerald-400 bg-emerald-900/30 border-emerald-800"
            )}`}>
            <FiArrowUpRight size={11} /> {trend}
          </span>
        )}
      </div>

      <div>
        <p className={`text-xs font-semibold uppercase tracking-widest mb-1
          ${t("text-gray-400", "text-gray-500")}`}>{label}</p>
        <p className={`text-3xl font-extrabold leading-none
          ${t("text-gray-900", "text-white")}`}>
          <Counter target={Number(value) || 0} />
        </p>
        {sub && <p className={`text-xs mt-1.5 ${t("text-gray-400", "text-gray-500")}`}>{sub}</p>}
      </div>
    </div>
  );
};

/* ─── Status Badge ─── */
const StatusBadge = ({ status }) => {
  const { t } = useTheme();
  const lightMap = {
    approved:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending:   "bg-amber-50 text-amber-700 border-amber-200",
    rejected:  "bg-red-50 text-red-700 border-red-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const darkMap = {
    approved:  "bg-emerald-900/30 text-emerald-400 border-emerald-800",
    pending:   "bg-amber-900/30 text-amber-400 border-amber-800",
    rejected:  "bg-red-900/30 text-red-400 border-red-800",
    completed: "bg-emerald-900/30 text-emerald-400 border-emerald-800",
  };
  return (
    <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border capitalize
      ${t(lightMap[status] || lightMap.pending, darkMap[status] || darkMap.pending)}`}>
      {status}
    </span>
  );
};

/* ─── Card wrapper ─── */
const Card = ({ title, badge, children, extra, className = "" }) => {
  const { t } = useTheme();
  return (
    <div
      className={`rounded-2xl border shadow-sm p-5 transition-colors duration-200
        ${t("bg-white border-red-50", "bg-gray-900 border-gray-700/60")} ${className}`}
      style={{ animation: "fadeUp 0.45s ease both" }}
    >
      {(title || badge || extra) && (
        <div className="flex items-center justify-between mb-4">
          <p className={`text-xs font-bold uppercase tracking-widest
            ${t("text-gray-500", "text-gray-400")}`}>{title}</p>
          <div className="flex items-center gap-2">
            {badge && (
              <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border
                ${t(
                  "bg-red-50 text-red-600 border-red-100",
                  "bg-red-900/30 text-red-400 border-red-800"
                )}`}>{badge}</span>
            )}
            {extra}
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

/* ─── Pie Legend ─── */
const PieLegend = ({ labels, data, colors }) => {
  const { t } = useTheme();
  return (
    <div className="mt-3 space-y-2">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colors[i] }} />
            <span className={`font-medium ${t("text-gray-500", "text-gray-400")}`}>{l}</span>
          </div>
          <span className={`font-bold ${t("text-gray-800", "text-white")}`}>{data[i]}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── Row item shared style ─── */
const useRowCls = () => {
  const { t } = useTheme();
  return `flex items-center gap-3 p-3 rounded-xl border border-transparent transition cursor-default
    ${t(
      "bg-gray-50 hover:bg-red-50/60 hover:border-red-100",
      "bg-gray-800/60 hover:bg-red-900/20 hover:border-red-900/50"
    )}`;
};

/* ─── User Row ─── */
const UserRow = ({ u }) => {
  const { t } = useTheme();
  const rowCls = useRowCls();
  return (
    <div className={rowCls}>
      {u.profileImage
        ? <img src={u.profileImage} alt={u.name}
            className={`w-10 h-10 rounded-full object-cover flex-shrink-0 shadow-sm border-2
              ${t("border-white", "border-gray-700")}`}
            onError={(e) => { e.target.style.display = "none"; }} />
        : <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm"
            style={{ background: getAvatarColor(u._id) }}>
            {getInitials(u.name || u.mobile || "?")}
          </div>
      }
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-bold truncate ${t("text-gray-800", "text-white")}`}>
          {u.name || u.mobile || "Unknown"}
        </p>
        <p className={`text-xs truncate ${t("text-gray-400", "text-gray-500")}`}>
          {u.email || u.mobile || "—"}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-sm shadow-emerald-200" />
        <span className={`text-[10px] hidden sm:block ${t("text-gray-400", "text-gray-500")}`}>
          {fmtDate(u.createdAt)}
        </span>
      </div>
    </div>
  );
};

/* ─── Chart axis / grid colors ─── */
const useChartColors = () => {
  const { dm } = useTheme();
  return {
    grid: dm ? "#374151" : "#f3f4f6",
    tick: dm ? "#6b7280" : "#9ca3af",
  };
};

/* ══════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════ */
const Dashboard = ({ darkMode = false, collapsed }) => {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab]   = useState("overview");

  const dm = !!darkMode;

  /* shorthand for inline conditional classes — used outside components */
  const t = (light, dark) => (dm ? dark : light);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const res  = await fetch(API_URL);
      const json = await res.json();
      if (json.success) setData(json);
      else setError("Failed to load dashboard data.");
    } catch {
      setError("Network error. Could not reach the server.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  /* ── Loading ── */
  if (loading) return (
    <ThemeCtx.Provider value={dm}>
      <div className={`min-h-screen flex flex-col items-center justify-center gap-4 transition-colors
        ${t(
          "bg-gradient-to-br from-red-50/60 via-white to-rose-50/40",
          "bg-gray-950"
        )}`}>
        <div className={`w-12 h-12 rounded-full border-[3px] border-t-red-500 animate-spin
          ${t("border-red-100", "border-red-900")}`} />
        <p className={`text-sm font-medium tracking-wide ${t("text-gray-400", "text-gray-500")}`}>
          Loading Dashboard…
        </p>
      </div>
    </ThemeCtx.Provider>
  );

  /* ── Error ── */
  if (error) return (
    <ThemeCtx.Provider value={dm}>
      <div className={`min-h-screen flex flex-col items-center justify-center gap-4 text-red-500
        ${t(
          "bg-gradient-to-br from-red-50/60 via-white to-rose-50/40",
          "bg-gray-950"
        )}`}>
        <FiAlertCircle size={40} />
        <p className="text-sm font-semibold">{error}</p>
        <button onClick={() => fetchData()}
          className="px-5 py-2 rounded-full bg-red-600 text-white text-sm font-bold shadow-md shadow-red-900/40 hover:bg-red-700 transition">
          Retry
        </button>
      </div>
    </ThemeCtx.Provider>
  );

  const { overview, users, products, charts, recent, topCategories, quickActions } = data;

  const PIE_COLORS = ["#dc2626", "#f97316", "#94a3b8"];
  const SUB_COLORS = ["#dc2626", "#94a3b8", "#b91c1c"];

  const monthlyData = charts?.monthly?.map((m) => ({
    name: m.month,
    Users: m.users,
    Products: m.products,
    Revenue: m.revenue,
  })) || [];

  const TABS = ["overview", "users", "products", "subscriptions"];

  const overviewStats = [
    { icon: FiUsers,      label: "Total Users",    value: overview.totalUsers,          sub: `${users.newToday} new today`,   iconBg: "bg-red-50",     iconBgDark: "bg-red-900/30",     iconColor: "text-red-600",     trend: `+${users.newToday}`, delay: 0   },
    { icon: FiPackage,    label: "Total Products",  value: overview.totalProducts,       sub: `${products.approved} approved`, iconBg: "bg-orange-50",  iconBgDark: "bg-orange-900/30",  iconColor: "text-orange-500",  trend: null,                 delay: 50  },
    { icon: FiGrid,       label: "Categories",      value: overview.totalCategories,     sub: "Across platform",               iconBg: "bg-violet-50",  iconBgDark: "bg-violet-900/30",  iconColor: "text-violet-600",  trend: null,                 delay: 100 },
    { icon: FiActivity,   label: "Active Subs",     value: overview.activeSubscriptions, sub: "All active plans",              iconBg: "bg-emerald-50", iconBgDark: "bg-emerald-900/30", iconColor: "text-emerald-600", trend: "Active",             delay: 150 },
    { icon: FiDollarSign, label: "Total Revenue",   value: overview.totalRevenue,        sub: "Lifetime earnings",             iconBg: "bg-yellow-50",  iconBgDark: "bg-yellow-900/30",  iconColor: "text-yellow-600",  trend: null,                 delay: 200 },
    { icon: FiUserCheck,  label: "Active (7 Days)", value: users.activeLast7Days,        sub: "Recently active users",         iconBg: "bg-sky-50",     iconBgDark: "bg-sky-900/30",     iconColor: "text-sky-600",     trend: "↑",                  delay: 250 },
    { icon: FiLayers,     label: "Sub-Categories",  value: overview.totalSubCategories,  sub: "Nested categories",             iconBg: "bg-pink-50",    iconBgDark: "bg-pink-900/30",    iconColor: "text-pink-600",    trend: null,                 delay: 300 },
    { icon: FiStar,       label: "Total Plans",     value: overview.totalPlans,          sub: "Available plans",               iconBg: "bg-amber-50",   iconBgDark: "bg-amber-900/30",   iconColor: "text-amber-600",   trend: null,                 delay: 350 },
  ];

  /* chart colors inside the render — needs dm */
  const chartGrid = dm ? "#374151" : "#f3f4f6";
  const chartTick = dm ? "#6b7280" : "#9ca3af";

  /* shared row class used inline */
  const rowCls = `flex items-center gap-3 p-3 rounded-xl border border-transparent transition cursor-default
    ${t(
      "bg-gray-50 hover:bg-red-50/60 hover:border-red-100",
      "bg-gray-800/60 hover:bg-red-900/20 hover:border-red-900/50"
    )}`;

  return (
    <ThemeCtx.Provider value={dm}>
      <>
        <style>{`
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(14px); }
            to   { opacity: 1; transform: translateY(0);    }
          }
        `}</style>

        <div className={`min-h-screen p-4 sm:p-6 lg:p-7 transition-colors duration-300
          ${t(
            "bg-gradient-to-br from-red-50/60 via-white to-rose-50/40",
            "bg-gray-950"
          )}`}>

          {/* ══ HEADER ══ */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-7"
            style={{ animation: "fadeUp 0.4s ease both" }}>

            {/* Title */}
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-8 h-8 rounded-xl bg-red-600 flex items-center justify-center shadow-md shadow-red-900/40 flex-shrink-0">
                  <FiActivity size={15} className="text-white" />
                </div>
                <h1 className={`text-2xl sm:text-3xl font-extrabold tracking-tight
                  ${t("text-gray-900", "text-white")}`}>
                  Admin <span className="text-red-500">Dashboard</span>
                </h1>
              </div>
              <p className={`text-xs font-medium pl-11 ${t("text-gray-400", "text-gray-500")}`}>
                Last updated · {new Date(data.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Bell */}
              <div className={`relative flex items-center gap-2 px-3 py-2 rounded-xl border
                shadow-sm text-xs font-semibold cursor-pointer transition select-none
                ${t(
                  "bg-white border-red-100 text-gray-600 hover:border-red-300",
                  "bg-gray-800 border-gray-700 text-gray-300 hover:border-red-700"
                )}`}>
                <FiBell size={14} className="text-red-500" />
                <span className="hidden sm:inline">Notifications</span>
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-600 text-white
                  text-[9px] font-bold rounded-full flex items-center justify-center px-1 shadow">
                  {quickActions.pendingNotifications}
                </span>
              </div>

              {/* Pills */}
              {[
                { label: "Pending Approvals", count: quickActions.pendingApprovals },
                { label: "Expiring Subs",     count: quickActions.expiringSubscriptions },
              ].map(({ label, count }) => (
                <div key={label}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border
                    shadow-sm text-xs font-semibold cursor-pointer transition select-none
                    ${t(
                      "bg-white border-red-100 text-gray-600 hover:border-red-300",
                      "bg-gray-800 border-gray-700 text-gray-300 hover:border-red-700"
                    )}`}>
                  <span className={`hidden sm:inline ${t("text-gray-400", "text-gray-500")}`}>{label}</span>
                  <span className={`min-w-[20px] h-5 rounded-full flex items-center justify-center text-[10px] font-bold px-1
                    ${count > 0
                      ? "bg-red-600 text-white shadow shadow-red-900/40"
                      : t("bg-gray-100 text-gray-400", "bg-gray-700 text-gray-500")
                    }`}>
                    {count}
                  </span>
                </div>
              ))}

              {/* Refresh */}
              <button onClick={() => fetchData(true)} disabled={refreshing}
                className="flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 active:bg-red-800
                  text-white rounded-xl text-xs font-bold shadow-md shadow-red-900/40
                  transition-all duration-150 disabled:opacity-60 select-none">
                <FiRefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
                <span className="hidden sm:inline">{refreshing ? "Refreshing…" : "Refresh"}</span>
              </button>
            </div>
          </div>

          {/* ══ TABS ══ */}
          <div className={`flex gap-1 p-1 rounded-xl shadow-sm w-fit mb-6 flex-wrap border
            ${t("bg-white border-red-100", "bg-gray-900 border-gray-700")}`}
            style={{ animation: "fadeUp 0.42s ease both" }}>
            {TABS.map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-xs font-bold capitalize transition-all duration-200 select-none
                  ${activeTab === tab
                    ? "bg-red-600 text-white shadow-md shadow-red-900/40"
                    : t(
                        "text-gray-400 hover:text-red-600 hover:bg-red-50",
                        "text-gray-500 hover:text-red-400 hover:bg-red-900/20"
                      )
                  }`}>
                {tab}
              </button>
            ))}
          </div>

          {/* ══════════════════ OVERVIEW ══════════════════ */}
          {activeTab === "overview" && (
            <>
              {/* Stat Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-5">
                {overviewStats.map((s) => <StatCard key={s.label} {...s} />)}
              </div>

              {/* Charts Row 1 — Area + Product Pie */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <Card title="Monthly Growth" badge="Last 6 months" className="lg:col-span-2">
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={monthlyData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                      <defs>
                        <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#dc2626" stopOpacity={dm ? 0.3 : 0.18} />
                          <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gP" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#f97316" stopOpacity={dm ? 0.3 : 0.18} />
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                      <XAxis dataKey="name" tick={{ fill: chartTick, fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: chartTick, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="Users"    stroke="#dc2626" strokeWidth={2.5} fill="url(#gU)" dot={{ r: 3, fill: "#dc2626", strokeWidth: 0 }} name="Users" />
                      <Area type="monotone" dataKey="Products" stroke="#f97316" strokeWidth={2.5} fill="url(#gP)" dot={{ r: 3, fill: "#f97316", strokeWidth: 0 }} name="Products" />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="flex items-center gap-5 mt-3">
                    {[{ l: "Users", c: "#dc2626" }, { l: "Products", c: "#f97316" }].map(({ l, c }) => (
                      <div key={l} className={`flex items-center gap-1.5 text-xs font-medium
                        ${t("text-gray-500", "text-gray-400")}`}>
                        <span className="w-4 h-1 rounded-full" style={{ background: c }} />{l}
                      </div>
                    ))}
                  </div>
                </Card>

                <Card title="Product Status" badge="Breakdown">
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={charts.productStatus.labels.map((l, i) => ({ name: l, value: charts.productStatus.data[i] }))}
                        cx="50%" cy="50%" innerRadius={48} outerRadius={70} dataKey="value" paddingAngle={4} strokeWidth={0}>
                        {charts.productStatus.labels.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <PieLegend labels={charts.productStatus.labels} data={charts.productStatus.data} colors={PIE_COLORS} />
                </Card>
              </div>

              {/* Charts Row 2 — Bar + Sub Pie */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <Card title="Revenue by Month" badge="₹ INR" className="lg:col-span-2">
                  <ResponsiveContainer width="100%" height={190}>
                    <BarChart data={monthlyData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                      <XAxis dataKey="name" tick={{ fill: chartTick, fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: chartTick, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="Revenue" fill="#dc2626" radius={[5, 5, 0, 0]} name="Revenue" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <Card title="Subscriptions" badge="Status">
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={charts.subscriptionStatus.labels.map((l, i) => ({ name: l, value: charts.subscriptionStatus.data[i] }))}
                        cx="50%" cy="50%" innerRadius={48} outerRadius={70} dataKey="value" paddingAngle={4} strokeWidth={0}>
                        {charts.subscriptionStatus.labels.map((_, i) => <Cell key={i} fill={SUB_COLORS[i]} />)}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <PieLegend labels={charts.subscriptionStatus.labels} data={charts.subscriptionStatus.data} colors={SUB_COLORS} />
                </Card>
              </div>

              {/* Recent Users + Products */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <Card title="Recent Users" badge={`${recent.users.length} shown`}>
                  <div className="space-y-2">
                    {recent.users.slice(0, 6).map((u) => <UserRow key={u._id} u={u} />)}
                  </div>
                </Card>

                <Card title="Recent Products" badge={`${recent.products.length} total`}>
                  <div className="space-y-2">
                    {recent.products.map((p) => (
                      <div key={p._id} className={rowCls}>
                        {p.images?.[0]
                          ? <img src={p.images[0]} alt={p.name}
                              className={`w-10 h-10 rounded-xl object-cover flex-shrink-0 shadow-sm border
                                ${t("border-gray-100", "border-gray-700")}`} />
                          : <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                                ${t("bg-red-50", "bg-red-900/30")}`}>
                              <FiPackage size={16} className="text-red-400" />
                            </div>
                        }
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold truncate ${t("text-gray-800", "text-white")}`}>{p.name}</p>
                          <p className={`text-xs ${t("text-gray-400", "text-gray-500")}`}>{p.category?.name || "—"}</p>
                        </div>
                        <StatusBadge status={p.status} />
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Top Categories */}
              <Card title="Top Categories" badge="By products">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {topCategories.map((cat) => (
                    <div key={cat._id}
                      className={`flex flex-col items-center p-4 rounded-xl border border-transparent
                        transition cursor-default group
                        ${t(
                          "bg-gray-50 hover:bg-red-50/60 hover:border-red-100",
                          "bg-gray-800/60 hover:bg-red-900/20 hover:border-red-900/50"
                        )}`}>
                      <img src={cat.image} alt={cat.name}
                        className={`w-12 h-12 rounded-xl object-cover mb-3 shadow-sm border
                          group-hover:scale-105 transition-transform duration-200
                          ${t("border-gray-100", "border-gray-700")}`}
                        onError={(e) => { e.target.src = "https://via.placeholder.com/48"; }} />
                      <p className={`text-xs font-bold text-center leading-tight mb-1.5
                        ${t("text-gray-700", "text-gray-200")}`}>{cat.name}</p>
                      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border
                        ${t(
                          "text-red-600 bg-red-50 border-red-100",
                          "text-red-400 bg-red-900/30 border-red-800"
                        )}`}>
                        {cat.productCount} products
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}

          {/* ══════════════════ USERS ══════════════════ */}
          {activeTab === "users" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <StatCard icon={FiUsers}     label="Total Users" value={users.total}           iconBg="bg-red-50"     iconBgDark="bg-red-900/30"     iconColor="text-red-600"     delay={0}   />
                <StatCard icon={FiUserCheck} label="Active (7d)" value={users.activeLast7Days} iconBg="bg-emerald-50" iconBgDark="bg-emerald-900/30" iconColor="text-emerald-600" delay={60}  sub="Last 7 days"  trend="Active" />
                <StatCard icon={FiCalendar}  label="New Today"   value={users.newToday}        iconBg="bg-orange-50"  iconBgDark="bg-orange-900/30"  iconColor="text-orange-500"  delay={120} sub="Joined today" trend={`+${users.newToday}`} />
              </div>

              <Card title="All Recent Users" badge={`${recent.users.length} records`}>
                <div className="space-y-2">
                  {recent.users.map((u) => <UserRow key={u._id} u={u} />)}
                </div>
              </Card>
            </>
          )}

          {/* ══════════════════ PRODUCTS ══════════════════ */}
          {activeTab === "products" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <StatCard icon={FiPackage}     label="Total"    value={products.total}    iconBg="bg-red-50"     iconBgDark="bg-red-900/30"     iconColor="text-red-600"     delay={0}   />
                <StatCard icon={FiCheckCircle} label="Approved" value={products.approved} iconBg="bg-emerald-50" iconBgDark="bg-emerald-900/30" iconColor="text-emerald-600" delay={60}  sub={`${products.approvedPercentage}% of total`} trend={`${products.approvedPercentage}%`} />
                <StatCard icon={FiClock}       label="Pending"  value={products.pending}  iconBg="bg-amber-50"   iconBgDark="bg-amber-900/30"   iconColor="text-amber-600"   delay={120} sub={`${products.pendingPercentage}% of total`} />
              </div>

              <Card title="All Products" badge={`${recent.products.length} total`} className="mb-4">
                <div className="space-y-3">
                  {recent.products.map((p) => (
                    <div key={p._id}
                      className={`flex items-center gap-4 p-4 rounded-xl border border-transparent transition cursor-default
                        ${t(
                          "bg-gray-50 hover:bg-red-50/50 hover:border-red-100",
                          "bg-gray-800/60 hover:bg-red-900/20 hover:border-red-900/50"
                        )}`}>
                      {p.images?.[0]
                        ? <img src={p.images[0]} alt={p.name}
                            className={`w-14 h-14 rounded-xl object-cover flex-shrink-0 shadow-sm border
                              ${t("border-gray-100", "border-gray-700")}`} />
                        : <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0
                              ${t("bg-red-50", "bg-red-900/30")}`}>
                            <FiPackage size={22} className="text-red-400" />
                          </div>
                      }
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${t("text-gray-900", "text-white")}`}>{p.name}</p>
                        <p className={`text-xs truncate mt-0.5 ${t("text-gray-400", "text-gray-500")}`}>{p.category?.name} · {p.address}</p>
                        <p className={`text-xs mt-0.5 ${t("text-gray-400", "text-gray-500")}`}>{p.attributes?.industry || p.attributes?.businessType || ""}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        <StatusBadge status={p.status} />
                        <span className={`text-[10px] ${t("text-gray-400", "text-gray-500")}`}>{fmtDate(p.createdAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card title="Top Categories">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {topCategories.map((cat) => (
                    <div key={cat._id}
                      className={`flex flex-col items-center p-4 rounded-xl border border-transparent
                        transition cursor-default group
                        ${t(
                          "bg-gray-50 hover:bg-red-50/60 hover:border-red-100",
                          "bg-gray-800/60 hover:bg-red-900/20 hover:border-red-900/50"
                        )}`}>
                      <img src={cat.image} alt={cat.name}
                        className={`w-12 h-12 rounded-xl object-cover mb-3 shadow-sm border
                          group-hover:scale-105 transition-transform
                          ${t("border-gray-100", "border-gray-700")}`}
                        onError={(e) => { e.target.src = "https://via.placeholder.com/48"; }} />
                      <p className={`text-xs font-bold text-center mb-1 ${t("text-gray-700", "text-gray-200")}`}>{cat.name}</p>
                      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border
                        ${t(
                          "text-red-600 bg-red-50 border-red-100",
                          "text-red-400 bg-red-900/30 border-red-800"
                        )}`}>
                        {cat.productCount}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}

          {/* ══════════════════ SUBSCRIPTIONS ══════════════════ */}
          {activeTab === "subscriptions" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <StatCard icon={FiActivity}    label="Active Subs" value={overview.activeSubscriptions}        iconBg="bg-red-50"     iconBgDark="bg-red-900/30"     iconColor="text-red-600"     delay={0}   />
                <StatCard icon={FiCheckCircle} label="Completed"   value={charts.subscriptionStatus.data[0]}  iconBg="bg-emerald-50" iconBgDark="bg-emerald-900/30" iconColor="text-emerald-600" delay={60}  />
                <StatCard icon={FiClock}       label="Expiring"    value={quickActions.expiringSubscriptions} iconBg="bg-amber-50"   iconBgDark="bg-amber-900/30"   iconColor="text-amber-600"   delay={120} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                <Card title="Subscriptions Over Time" badge="Monthly" className="lg:col-span-2">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={monthlyData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                      <defs>
                        <linearGradient id="gSubL" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#dc2626" stopOpacity={dm ? 0.3 : 0.18} />
                          <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                      <XAxis dataKey="name" tick={{ fill: chartTick, fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: chartTick, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Area type="monotone" dataKey="Users" stroke="#dc2626" strokeWidth={2.5} fill="url(#gSubL)"
                        dot={{ r: 3, fill: "#dc2626", strokeWidth: 0 }} name="Users" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>

                <Card title="Status Breakdown">
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={charts.subscriptionStatus.labels.map((l, i) => ({ name: l, value: charts.subscriptionStatus.data[i] }))}
                        cx="50%" cy="50%" innerRadius={48} outerRadius={70} dataKey="value" paddingAngle={4} strokeWidth={0}>
                        {charts.subscriptionStatus.labels.map((_, i) => <Cell key={i} fill={SUB_COLORS[i]} />)}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <PieLegend labels={charts.subscriptionStatus.labels} data={charts.subscriptionStatus.data} colors={SUB_COLORS} />
                </Card>
              </div>

              <Card title="Recent Subscriptions" badge={`${recent.subscriptions.length} records`}>
                <div className="space-y-2">
                  {recent.subscriptions.map((s) => (
                    <div key={s._id} className={rowCls}>
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border
                        ${t(
                          "bg-red-50 border-red-100",
                          "bg-red-900/30 border-red-800"
                        )}`}>
                        <FiActivity size={14} className="text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${t("text-gray-800", "text-white")}`}>
                          {s.user?.name || s.user?.email || "Anonymous User"}
                        </p>
                        <p className={`text-xs ${t("text-gray-400", "text-gray-500")}`}>
                          {s.plan?.name} · {s.plan?.price === 0 ? "Free" : `₹${s.plan?.price}`} · Limit: {s.productsLimit}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <StatusBadge status={s.paymentStatus} />
                        <span className={`text-[10px] ${t("text-gray-400", "text-gray-500")}`}>
                          Ends {fmtDate(s.endDate)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}

        </div>
      </>
    </ThemeCtx.Provider>
  );
};

export default Dashboard;